import {
  SEO_PAGE_META_EDITABLE_FIELDS,
  SEO_PAGE_PATH_PATTERNS,
  SEO_PAGE_PLACEHOLDERS,
  SeoPageMetaContent,
  SeoPageMetaEditableField,
  SeoPageMetaField,
  SeoPageMetaSnapshot,
  SeoPageType,
  SeoPlaceholder,
  SeoPlaceholderValues,
} from '@pflegia/types/common/seo-page-meta/seo-page-meta';

export type SeoMarkupError = 'tagsNotAllowed' | 'invalidHighlight';
export type SeoPageMetaVersion<T> = T & { snapshot: SeoPageMetaSnapshot; changedFields: SeoPageMetaEditableField[] };

export interface HighlightedTextSegment {
  text: string;
  highlighted: boolean;
}

const PLACEHOLDER_REGEX = /\{(\w+)\}/g;
const INVALID_PLACEHOLDER_REGEX = /\{[^{}]*\}|\{[^{}\s]*|\}/g;
const HIGHLIGHT_REGEX = /<span>([^<>]*)<\/span>/g;
const ANGLE_BRACKET_REGEX = /[<>]/;
const PAGE_NUMBER_REGEX = /^\d+$/;

const isBlank = (value: string | null | undefined): value is null | undefined | '' => !value?.trim();
const isMissingValue = (values: SeoPlaceholderValues, key: string): boolean => {
  const value = values[key as SeoPlaceholder];
  return value === undefined || value === null || value === '';
};
export const stripSeoMarkup = (text: string): string => text.replace(/<\/?span>/g, '');

// Replaces `{placeholder}` tokens with page values; returns null if the template is empty or a used placeholder has no value
export const fillSeoTemplate = (template: string | null | undefined, values: SeoPlaceholderValues): string | null => {
  if (isBlank(template)) return null;

  let hasMissingValue = false;
  const filled = template.replace(PLACEHOLDER_REGEX, (_match, key: string) => {
    if (isMissingValue(values, key)) {
      hasMissingValue = true;
      return '';
    }
    return String(values[key as SeoPlaceholder]);
  });

  return hasMissingValue ? null : filled.trim();
};

// Returns the placeholders of a template that have no value for the page, each once
export const findMissingPlaceholders = (template: string | null | undefined, values: SeoPlaceholderValues): string[] => {
  if (!template) return [];

  const placeholders = Array.from(template.matchAll(PLACEHOLDER_REGEX), ([, key]) => key);

  return placeholders.filter((key, index) => isMissingValue(values, key) && placeholders.indexOf(key) === index);
};

// Returns the brace fragments that are not a valid `{placeholder}`, e.g. `{city state}` or an unclosed `{city`, each once
export const findInvalidPlaceholderSyntax = (template: string | null | undefined): string[] => {
  if (!template) return [];

  const fragments = Array.from(template.replace(PLACEHOLDER_REGEX, '').matchAll(INVALID_PLACEHOLDER_REGEX), ([fragment]) => fragment);

  return fragments.filter((fragment, index) => fragments.indexOf(fragment) === index);
};

// Returns true if the template uses the given placeholder
export const hasPlaceholder = (template: string | null | undefined, placeholder: SeoPlaceholder): boolean =>
  Boolean(template?.includes(`{${placeholder}}`));

// Fills title, description and h1 with the page values; each field falls back to null independently
export const fillSeoPageMeta = (
  meta: Partial<SeoPageMetaContent> | null | undefined,
  values: SeoPlaceholderValues,
): SeoPageMetaContent => ({
  title: fillSeoTemplate(meta?.title, values),
  description: fillSeoTemplate(meta?.description, values),
  h1: fillSeoTemplate(meta?.h1, values),
});

// Returns the placeholders used in a template that are not available for the page type, each once
export const findUnknownPlaceholders = (template: string | null | undefined, pageType: SeoPageType): string[] => {
  if (!template) return [];

  const allowed: string[] = SEO_PAGE_PLACEHOLDERS[pageType];
  const placeholders = Array.from(template.matchAll(PLACEHOLDER_REGEX), ([, key]) => key);

  return placeholders.filter((key, index) => !allowed.includes(key) && placeholders.indexOf(key) === index);
};

// Rejects any HTML in title and description; the h1 may only contain plain `<span>…</span>` highlights
export const validateSeoMarkup = (text: string | null | undefined, field: SeoPageMetaField): SeoMarkupError | null => {
  if (!text) return null;

  if (field !== SeoPageMetaField.H1) return ANGLE_BRACKET_REGEX.test(text) ? 'tagsNotAllowed' : null;

  return ANGLE_BRACKET_REGEX.test(text.replace(HIGHLIGHT_REGEX, '$1')) ? 'invalidHighlight' : null;
};

// Splits a text into plain and highlighted segments based on its `<span>` tags, for rendering without innerHTML
export const splitHighlightedText = (text: string): HighlightedTextSegment[] => {
  const segments: HighlightedTextSegment[] = [];
  let lastIndex = 0;

  Array.from(text.matchAll(HIGHLIGHT_REGEX)).forEach(match => {
    segments.push({ text: text.slice(lastIndex, match.index), highlighted: false }, { text: match[1], highlighted: true });
    lastIndex = match.index + match[0].length;
  });
  segments.push({ text: text.slice(lastIndex), highlighted: false });

  return segments.filter(segment => segment.text !== '');
};

// Turns a URL or path into the stored form (`/jobs/a/b`): lowercase, no domain, query or page number; null if not a /jobs or /gehalt page
export const normalizeSeoPagePath = (input: string | null | undefined): string | null => {
  const trimmed = input?.trim();
  if (!trimmed) return null;

  let url: URL;
  try {
    url = new URL(trimmed, 'https://placeholder.invalid');
  } catch {
    return null;
  }
  const { pathname } = url;

  const [section, ...slugs] = pathname.toLowerCase().split('/').filter(Boolean);
  if (section !== 'jobs' && section !== 'gehalt') return null;
  if (section === 'jobs' && PAGE_NUMBER_REGEX.test(slugs.at(-1) ?? '')) slugs.pop();
  if (!slugs.length) return null;

  return `/${[section, ...slugs].join('/')}`;
};

// Checks that a normalized path has the section and number of slugs expected for the page type
export const isPathValidForPageType = (path: string, pageType: SeoPageType): boolean => {
  const [section, ...slugs] = path.split('/').filter(Boolean);
  const pattern = SEO_PAGE_PATH_PATTERNS[pageType];

  return section === pattern.section && slugs.length === pattern.slugCount;
};

// Combines override and page type template per field: the override wins, blank values count as not set
export const mergeSeoPageMeta = (
  override: Partial<SeoPageMetaContent> | null | undefined,
  template: Partial<SeoPageMetaContent> | null | undefined,
): SeoPageMetaContent => {
  const pick = (field: SeoPageMetaField): string | null => {
    const overrideValue = override?.[field];
    if (!isBlank(overrideValue)) return overrideValue;
    const templateValue = template?.[field];
    return isBlank(templateValue) ? null : templateValue;
  };

  return {
    title: pick(SeoPageMetaField.Title),
    description: pick(SeoPageMetaField.Description),
    h1: pick(SeoPageMetaField.H1),
  };
};

// Replays history entries in chronological order and attaches the full snapshot and changed fields to each entry
export const buildSeoPageMetaVersions = <T extends { newValue: Record<string, unknown> }>(
  chronologicalHistory: T[],
): SeoPageMetaVersion<T>[] => {
  let snapshot: SeoPageMetaSnapshot = Object.fromEntries(SEO_PAGE_META_EDITABLE_FIELDS.map(field => [field, null]));

  return chronologicalHistory.map(entry => {
    const changedFields = SEO_PAGE_META_EDITABLE_FIELDS.filter(field => field in entry.newValue);
    snapshot = {
      ...snapshot,
      ...Object.fromEntries(changedFields.map(field => [field, (entry.newValue[field] as string | null | undefined) ?? null])),
    };
    return { ...entry, snapshot, changedFields };
  });
};
